<template>
    <Transition name="billing-fade">
        <div v-if="bs.state.checkoutOpen" class="billing-checkout-overlay" @click.self="onOverlayClick">
            <div class="billing-checkout-card">
                <div v-if="step <= 3" class="billing-checkout-steps">
                    <span :class="['billing-step', step >= 1 ? 'billing-step--active' : '', step > 1 ? 'billing-step--done' : '']">
                        <span class="billing-step-number">{{ step > 1 ? '\u2713' : '1' }}</span>
                        <span>{{ $t('common.billing.stepAddress') }}</span>
                    </span>
                    <span :class="['billing-step-connector', step > 1 ? 'billing-step-connector--done' : '']"></span>
                    <span :class="['billing-step', step >= 2 ? 'billing-step--active' : '', step > 2 ? 'billing-step--done' : '']">
                        <span class="billing-step-number">{{ step > 2 ? '\u2713' : '2' }}</span>
                        <span>{{ $t('common.billing.stepPayment') }}</span>
                    </span>
                    <span :class="['billing-step-connector', step > 2 ? 'billing-step-connector--done' : '']"></span>
                    <span :class="['billing-step', step >= 3 ? 'billing-step--active' : '']">
                        <span class="billing-step-number">3</span>
                        <span>{{ $t('common.billing.stepConfirm') }}</span>
                    </span>
                </div>

                <div class="billing-checkout-body">
                    <template v-if="step <= 3">
                        <div class="billing-checkout-summary">
                            <span class="billing-checkout-summary-name">{{ item?.name }}</span>
                            <span class="billing-checkout-summary-price">{{ bs.formatPrice(item?.amount || 0) }}&#8378;</span>
                        </div>
                    </template>

                    <template v-if="step === 1">
                        <div class="billing-checkout-section-title">{{ $t('common.billing.billingAddress') }}</div>
                        <div class="billing-checkout-address-list">
                            <div v-for="addr in bs.ADDRESSES" :key="addr.id"
                                 :class="['billing-checkout-address', {'billing-checkout-address--selected': bs.state.checkoutAddressId === addr.id}]"
                                 @click="bs.state.checkoutAddressId = addr.id">
                                <span class="billing-checkout-radio">
                                    <span class="billing-checkout-radio-dot"></span>
                                </span>
                                <div>
                                    <div class="billing-checkout-address-label">{{ addr.label }}{{ addr.isDefault ? ' (' + $t('common.billing.default') + ')' : '' }}</div>
                                    <div class="billing-checkout-address-line">{{ addr.line }}, {{ addr.district }}, {{ addr.city }}</div>
                                </div>
                            </div>
                        </div>
                        <button v-if="!bs.state.addAddressOpen" class="billing-checkout-add-btn" @click="bs.toggleAddAddress()">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                            {{ $t('common.billing.addAddress') }}
                        </button>
                        <div v-if="bs.state.addAddressOpen" class="billing-checkout-address-form">
                            <div class="billing-form-title">{{ $t('common.billing.addNewAddress') }}</div>
                            <div class="billing-form-row">
                                <div class="billing-form-group">
                                    <label class="billing-form-label">{{ $t('common.billing.addressTitle') }}</label>
                                    <input class="billing-form-input" type="text" v-model="newAddress.label" :placeholder="$t('common.billing.addressTitlePlaceholder')">
                                </div>
                            </div>
                            <div class="billing-form-row">
                                <div class="billing-form-group">
                                    <label class="billing-form-label">{{ $t('common.billing.addressLine') }}</label>
                                    <input class="billing-form-input" type="text" v-model="newAddress.line" :placeholder="$t('common.billing.addressLinePlaceholder')">
                                </div>
                            </div>
                            <div class="billing-form-row">
                                <div class="billing-form-group">
                                    <label class="billing-form-label">{{ $t('common.billing.district') }}</label>
                                    <input class="billing-form-input" type="text" v-model="newAddress.district" placeholder="Kadıköy">
                                </div>
                                <div class="billing-form-group">
                                    <label class="billing-form-label">{{ $t('common.billing.city') }}</label>
                                    <input class="billing-form-input" type="text" v-model="newAddress.city" placeholder="İstanbul">
                                </div>
                            </div>
                            <div class="billing-form-actions">
                                <button class="billing-btn billing-btn--ghost" @click="bs.toggleAddAddress()">{{ $t('common.billing.cancelAction') }}</button>
                                <button class="billing-btn billing-btn--primary" :disabled="!canSaveAddress" @click="onSaveAddress">{{ $t('common.billing.saveAddress') }}</button>
                            </div>
                        </div>
                    </template>

                    <template v-if="step === 2">
                        <div class="billing-checkout-section-title">{{ $t('common.billing.paymentMethod') }}</div>
                        <div class="billing-checkout-payment-tabs">
                            <button :class="['billing-checkout-payment-tab', {'billing-checkout-payment-tab--active': bs.state.checkoutPaymentMethod === 'card'}]"
                                    @click="bs.state.checkoutPaymentMethod = 'card'">{{ $t('common.billing.creditCard') }}</button>
                            <button :class="['billing-checkout-payment-tab', {'billing-checkout-payment-tab--active': bs.state.checkoutPaymentMethod === 'wallet'}]"
                                    @click="bs.state.checkoutPaymentMethod = 'wallet'">{{ $t('common.billing.walletPay') }}</button>
                            <button :class="['billing-checkout-payment-tab', {'billing-checkout-payment-tab--active': bs.state.checkoutPaymentMethod === 'transfer'}]"
                                    @click="bs.state.checkoutPaymentMethod = 'transfer'">{{ $t('common.billing.bankTransfer') }}</button>
                        </div>

                        <template v-if="bs.state.checkoutPaymentMethod === 'card'">
                            <div class="billing-checkout-card-list">
                                <div v-for="card in bs.CARDS" :key="card.id"
                                     :class="['billing-checkout-card-item', {'billing-checkout-card-item--selected': bs.state.checkoutCardId === card.id}]"
                                     @click="bs.state.checkoutCardId = card.id">
                                    <span class="billing-checkout-radio">
                                        <span class="billing-checkout-radio-dot"></span>
                                    </span>
                                    <span :class="['billing-card-brand', 'billing-card-brand--' + card.brand]">{{ card.brand === 'visa' ? 'VISA' : 'MC' }}</span>
                                    <span class="billing-checkout-card-number">&bull;&bull;&bull;&bull; {{ card.number }}</span>
                                    <span class="billing-checkout-card-expiry">{{ card.expiry }}</span>
                                </div>
                                <div :class="['billing-checkout-card-item', {'billing-checkout-card-item--selected': bs.state.checkoutCardId === 'new'}]"
                                     @click="bs.state.checkoutCardId = 'new'">
                                    <span class="billing-checkout-radio">
                                        <span class="billing-checkout-radio-dot"></span>
                                    </span>
                                    <span class="billing-checkout-newcard-label">{{ $t('common.billing.payWithNewCard') }}</span>
                                </div>
                            </div>
                            <Transition name="billing-slide">
                                <div v-if="bs.state.checkoutCardId === 'new'" class="billing-checkout-newcard-form">
                                    <div class="billing-form-row">
                                        <div class="billing-form-group">
                                            <label class="billing-form-label">{{ $t('common.billing.cardNumber') }}</label>
                                            <input class="billing-form-input" type="text" v-model="bs.state.checkoutNewCard.number" placeholder="0000 0000 0000 0000" maxlength="19" @input="onCardNumberInput">
                                        </div>
                                    </div>
                                    <div class="billing-form-row">
                                        <div class="billing-form-group">
                                            <label class="billing-form-label">{{ $t('common.billing.cardExpiry') }}</label>
                                            <input class="billing-form-input" type="text" v-model="bs.state.checkoutNewCard.expiry" placeholder="AA/YY" maxlength="5" @input="onExpiryInput">
                                        </div>
                                        <div class="billing-form-group">
                                            <label class="billing-form-label">CVV</label>
                                            <input class="billing-form-input" type="text" v-model="bs.state.checkoutNewCard.cvv" placeholder="000" maxlength="4">
                                        </div>
                                    </div>
                                    <div class="billing-form-row">
                                        <div class="billing-form-group">
                                            <label class="billing-form-label">{{ $t('common.billing.cardHolder') }}</label>
                                            <input class="billing-form-input" type="text" v-model="bs.state.checkoutNewCard.holder" :placeholder="$t('common.billing.cardHolderPlaceholder')">
                                        </div>
                                    </div>
                                </div>
                            </Transition>
                        </template>

                        <div class="billing-checkout-section-title">{{ $t('common.billing.discountCode') }}</div>
                        <template v-if="bs.state.checkoutDiscount">
                            <div class="billing-coupon-card">
                                <div class="billing-coupon-card-bar"></div>
                                <div class="billing-coupon-card-body">
                                    <div class="billing-coupon-card-header">
                                        <span class="billing-coupon-card-code">{{ bs.state.checkoutDiscount.code }}</span>
                                        <button class="billing-coupon-card-remove" @click="onRemoveDiscount">
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                            {{ $t('common.billing.remove') }}
                                        </button>
                                    </div>
                                    <div class="billing-coupon-card-label">{{ bs.state.checkoutDiscount.label }}</div>
                                    <div class="billing-coupon-card-detail">
                                        <span class="billing-coupon-card-percent">{{ discountDescription }}</span>
                                        <span class="billing-coupon-card-dot">&middot;</span>
                                        <span class="billing-coupon-card-savings">{{ bs.formatPrice(discountAmount) }}&#8378; {{ $t('common.billing.savings') }}</span>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="billing-checkout-discount">
                                <input class="billing-checkout-discount-input" type="text" v-model="discountCode" :placeholder="$t('common.billing.enterCode')" @keyup.enter="onApplyDiscount">
                                <button class="billing-btn billing-btn--outline" @click="onApplyDiscount">{{ $t('common.billing.apply') }}</button>
                            </div>
                            <div v-if="bs.state.checkoutDiscountError" class="billing-checkout-discount-error">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                {{ $t('common.billing.invalidCode') }}
                            </div>
                        </template>
                    </template>

                    <template v-if="step === 3">
                        <div class="billing-checkout-section-title">{{ $t('common.billing.orderSummary') }}</div>
                        <table class="billing-checkout-confirm-table">
                            <tr>
                                <td>{{ $t('common.billing.product') }}</td>
                                <td>{{ bs.formatPrice(item?.amount || 0) }}&#8378;</td>
                            </tr>
                            <tr v-if="bs.state.checkoutDiscount" class="billing-checkout-discount-row">
                                <td>{{ $t('common.billing.discount') }}</td>
                                <td>-{{ bs.formatPrice(discountAmount) }}&#8378;</td>
                            </tr>
                            <tr class="billing-checkout-total-row">
                                <td>{{ $t('common.billing.total') }}</td>
                                <td>{{ bs.formatPrice(totalAmount) }}&#8378;</td>
                            </tr>
                        </table>
                        <table class="billing-checkout-confirm-table billing-checkout-confirm-detail">
                            <tr>
                                <td>{{ $t('common.billing.paymentLabel') }}</td>
                                <td>{{ selectedPaymentLabel }}</td>
                            </tr>
                            <tr>
                                <td>{{ $t('common.billing.addressLabel') }}</td>
                                <td>{{ selectedAddressLabel }}</td>
                            </tr>
                        </table>
                        <label class="billing-checkout-agree">
                            <input type="checkbox" v-model="bs.state.checkoutAgreed">
                            {{ $t('common.billing.agreeTerms') }}
                        </label>
                    </template>

                    <template v-if="step === 4">
                        <div class="billing-checkout-result">
                            <template v-if="bs.state.paymentStatus === 'processing'">
                                <div class="billing-result-icon billing-result-icon--spin">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                                </div>
                                <div class="billing-result-title">{{ $t('common.billing.processing') }}</div>
                                <div class="billing-result-desc">{{ $t('common.billing.processingDesc') }}</div>
                            </template>
                            <template v-else-if="bs.state.paymentStatus === 'success'">
                                <div class="billing-result-icon billing-result-icon--success billing-result-icon--pop">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                                </div>
                                <div class="billing-result-title">{{ $t('common.billing.paymentSuccess') }}</div>
                                <div class="billing-result-order">#{{ bs.state.paymentOrderId }} &middot; {{ item?.name }}</div>
                                <div class="billing-result-amount">{{ bs.formatPrice(totalAmount) }}&#8378;</div>
                            </template>
                            <template v-else-if="bs.state.paymentStatus === 'failed'">
                                <div class="billing-result-icon billing-result-icon--failed billing-result-icon--shake">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                </div>
                                <div class="billing-result-title">{{ $t('common.billing.paymentFailed') }}</div>
                                <div class="billing-result-desc">{{ bs.state.paymentError }}</div>
                            </template>
                        </div>
                    </template>

                    <div class="billing-checkout-footer">
                        <template v-if="step <= 3">
                            <button v-if="step > 1" class="billing-btn billing-btn--ghost" @click="bs.prevStep()">{{ $t('common.billing.back') }}</button>
                            <button v-else class="billing-btn billing-btn--ghost" @click="bs.closeCheckout()">{{ $t('common.billing.cancelAction') }}</button>
                            <button v-if="step < 3" class="billing-btn billing-btn--primary" :disabled="!canContinue" @click="bs.nextStep()">{{ $t('common.billing.continue') }}</button>
                            <button v-else class="billing-btn billing-btn--primary" :disabled="!bs.state.checkoutAgreed" @click="bs.processPayment()">
                                {{ $t('common.billing.confirmOrder') }} &#10003;
                            </button>
                        </template>
                        <template v-else-if="step === 4 && bs.state.paymentStatus !== 'processing'">
                            <button class="billing-btn billing-btn--ghost" @click="bs.closeCheckout()">{{ $t('common.billing.close') }}</button>
                            <button v-if="bs.state.paymentStatus === 'success'" class="billing-btn billing-btn--primary" @click="onGoToOrders">{{ $t('common.billing.goToOrders') }}</button>
                            <button v-else-if="bs.state.paymentStatus === 'failed'" class="billing-btn billing-btn--primary" @click="bs.retryPayment()">{{ $t('common.billing.retry') }}</button>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </Transition>
</template>

<script>
import billingService from './billingService'

export default {
    name: 'BillingCheckout',
    data() {
        return {
            discountCode: 'HOSGELDIN',
            newAddress: {
                label: '',
                line: '',
                district: '',
                city: '',
            },
        }
    },
    computed: {
        bs() {
            return billingService
        },
        step() {
            return billingService.state.checkoutStep
        },
        item() {
            return billingService.state.checkoutItem
        },
        discountAmount() {
            const d = billingService.state.checkoutDiscount
            if (!d || !this.item) return 0
            if (d.type === 'percent') return Math.round(this.item.amount * d.value / 100)
            if (d.type === 'fixed') return Math.min(d.value, this.item.amount)
            return 0
        },
        discountDescription() {
            const d = billingService.state.checkoutDiscount
            if (!d) return ''
            if (d.type === 'percent') return '%' + d.value + ' ' + this.$t('common.billing.discountWord')
            return d.value + '\u20BA ' + this.$t('common.billing.discountWord')
        },
        totalAmount() {
            return (this.item?.amount || 0) - this.discountAmount
        },
        selectedPaymentLabel() {
            if (billingService.state.checkoutPaymentMethod === 'wallet') return this.$t('common.billing.walletPay')
            if (billingService.state.checkoutPaymentMethod === 'transfer') return this.$t('common.billing.bankTransfer')
            if (billingService.state.checkoutCardId === 'new') return this.$t('common.billing.payWithNewCard')
            const card = billingService.CARDS.find(c => c.id === billingService.state.checkoutCardId)
            return card ? (card.brand === 'visa' ? 'Visa' : 'MC') + ' **** ' + card.number : ''
        },
        selectedAddressLabel() {
            const addr = billingService.ADDRESSES.find(a => a.id === billingService.state.checkoutAddressId)
            return addr ? addr.label + ', ' + addr.district : ''
        },
        canSaveAddress() {
            const a = this.newAddress
            return a.label.trim() && a.line.trim() && a.district.trim() && a.city.trim()
        },
        canContinue() {
            if (this.step === 2 && billingService.state.checkoutPaymentMethod === 'card' && billingService.state.checkoutCardId === 'new') {
                const c = billingService.state.checkoutNewCard
                return c.number.replace(/\s/g, '').length >= 15 && c.expiry.length === 5 && c.cvv.length >= 3 && c.holder.trim().length > 1
            }
            return true
        },
    },
    methods: {
        onApplyDiscount() {
            billingService.applyDiscount(this.discountCode)
        },
        onRemoveDiscount() {
            billingService.clearDiscount()
            this.discountCode = ''
        },
        onSaveAddress() {
            if (!this.canSaveAddress) return
            billingService.addAddress({ ...this.newAddress })
            this.newAddress = { label: '', line: '', district: '', city: '' }
        },
        onGoToOrders() {
            billingService.closeCheckout()
            billingService.setSection('orders')
        },
        onOverlayClick() {
            if (this.step === 4) return
            billingService.closeCheckout()
        },
        onCardNumberInput(e) {
            let v = e.target.value.replace(/\D/g, '').substring(0, 16)
            v = v.replace(/(.{4})/g, '$1 ').trim()
            billingService.state.checkoutNewCard.number = v
        },
        onExpiryInput(e) {
            let v = e.target.value.replace(/\D/g, '').substring(0, 4)
            if (v.length > 2) v = v.substring(0, 2) + '/' + v.substring(2)
            billingService.state.checkoutNewCard.expiry = v
        },
    },
    watch: {
        'bs.state.checkoutOpen'(val) {
            if (val) {
                this.discountCode = 'HOSGELDIN'
                this.newAddress = { label: '', line: '', district: '', city: '' }
            }
        },
    },
}
</script>
